/**
 * Where an element's animation tracks live.
 *
 * Upstream's Element has no notion of per-element tracks (only the camera
 * animates), so the map hangs off a WeakMap instead of a patched class:
 * elements come and go with the scene and their tracks go with them.
 */

import type { AnimTrack } from '../anim-track';
import type { Element } from '../element';

const elementTracks = new WeakMap<Element, Map<string, AnimTrack>>();

/** The element's tracks, keyed by effect id (created on first use). */
export const tracksOf = (element: Element): Map<string, AnimTrack> => {
    let map = elementTracks.get(element);
    if (!map) {
        map = new Map();
        elementTracks.set(element, map);
    }
    return map;
};

export const hasTracks = (element: Element): boolean => (elementTracks.get(element)?.size ?? 0) > 0;

/** One row of the timeline: the camera, or one effect on one element. */
export type TrackInfo = {
    id: string;
    kind: 'camera' | 'effect';
    elementName: string;
    effectName: string;
    element: Element | null;
    effectId: string | null;
    track: AnimTrack;
};
